/**
 * <subscription-options>
 *
 * Keeps the two cards in sync with the selected card, quantity and variant.
 * Prices come from the JSON block rendered by the snippet: one-time from the
 * variant, subscription from that variant's selling plan allocation.
 */
if (!customElements.get('subscription-options')) {
  customElements.define(
    'subscription-options',
    class SubscriptionOptions extends HTMLElement {
      connectedCallback() {
        this.moneyFormat = this.dataset.moneyFormat || '£{{amount}}';
        // Localised by the snippet. Repainting this line from an English literal
        // left every non-English storefront saying "per day" the moment the price
        // was recalculated, even though the server had rendered it translated.
        this.perDayLabel = this.dataset.perDayLabel || 'per day';
        this.hasBulkBuy = this.dataset.hasBulkBuy === 'true';
        // In a quick view, look for the variant, tiles and quantity inside the popup's own
        // product only. Searched page-wide they were the first product card's (collection
        // page) or the main product's (product page), so the popup's prices never moved.
        this.root = (this.closest('quick-add-modal, .quick-add-modal') && this.closest('product-info')) || document;
        this.servings = parseInt(this.dataset.servings || '0', 10);
        // A tier stated by collection membership is the product's tier whatever
        // interval is chosen. Only an inferred tier follows the interval.
        this.lockedTier = parseInt(this.dataset.lockedTier || '0', 10);
        // Off where the copy already names the order; see the snippet.
        this.showOrderNumbers = this.dataset.showOrderNumbers !== 'false';

        this.data = this.readPrices();
        this.plans = this.data.plans || [];
        this.defaultPlanId = this.dataset.defaultPlan || '';
        this.baseMonths = parseInt(this.dataset.baseMonths || '0', 10);
        this.scaleWithQuantity = this.dataset.scaleWithQuantity === 'true';
        this.freqEl = this.querySelector('.po-card__freq');
        this.picker = this.querySelector('.po-freq-picker');
        this.dropdown = this.querySelector('freq-dropdown');
        this.benefitsEl = this.querySelector('.po-benefits');

        // Set only when the customer picks an interval themselves, so their choice
        // survives repaints but is dropped when the quantity changes what it means.
        this.chosenPlanId = null;
        this.pickerQty = null;

        this.onPickerChange = this.onPickerChange.bind(this);
        this.addEventListener('freq:change', this.onPickerChange);
        this.cards = Array.from(this.querySelectorAll('.po-card'));
        this.subCard = this.querySelector('.po-card--sub');
        this.onceCard = this.querySelector('.po-card--once');
        this.planInput = this.querySelector('.po-selling-plan');
        // Pack rows inside the cards (bulk buy products). They only drive the bulk buy
        // tiles, which stay on the page hidden, so every existing quantity rule still runs.
        this.packsOn = this.classList.contains('po--packs');

        this.onCardChange = this.onCardChange.bind(this);
        this.onOutsideChange = this.onOutsideChange.bind(this);
        this.update = this.update.bind(this);

        // Click handles the label, change handles keyboard arrow navigation
        this.addEventListener('click', this.onCardChange);
        this.addEventListener('change', this.onCardChange);

        // Bulk tiles and the quantity stepper live outside this element. Listened for on
        // the document rather than on this section, because the sticky buy bar is a section
        // of its own and its stepper never reached a section-scoped listener - so changing
        // the quantity down there left these cards, and the bar's own price, on the old one.
        // Cart steppers share the same class names and are filtered out in onOutsideChange.
        document.addEventListener('click', this.onOutsideChange);
        document.addEventListener('change', this.onOutsideChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () =>
            requestAnimationFrame(this.update)
          );
        }

        this.syncPlanInput();
        this.update();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onCardChange);
        this.removeEventListener('change', this.onCardChange);
        document.removeEventListener('click', this.onOutsideChange);
        document.removeEventListener('change', this.onOutsideChange);
        this.unsubscribe?.();
      }

      readPrices() {
        try {
          return JSON.parse(this.querySelector('.po-variant-data').textContent);
        } catch (error) {
          console.warn('subscription-options: bad variant data', error);
          return {};
        }
      }

      /* ── Selection ─────────────────────────────────────────────────────── */

      onCardChange(event) {
        // The interval picker lives inside the Subscribe card, so its clicks and
        // change events bubble here. Handled by onPickerChange instead.
        if (event.target.closest('.po-freq-picker') || event.target.closest('freq-dropdown')) return;

        const card = event.target.closest('.po-card');
        if (!card) return;

        this.cards.forEach((item) => {
          const active = item === card;
          item.classList.toggle('is-active', active);
          const radio = item.querySelector('.po-radio');
          if (radio) radio.checked = active;
        });

        this.syncPlanInput();

        const pack = event.type === 'click' ? event.target.closest('.po-pack') : null;
        if (pack && this.choosePack(parseInt(pack.dataset.qty || '1', 10))) return;

        this.update();
      }

      /**
       * A pack row was picked: click the bulk buy tile for that quantity, exactly as the
       * customer used to. That one click is what product-custom.js, product-form.js, the
       * Grab at box and onOutsideChange() all listen for, so nothing is re-implemented here.
       * Returns false when there is no tile to click (the caller repaints instead).
       */
      choosePack(qty) {
        const inner = this.root.querySelector('.main-bulk-buy .bulk-buy-inner:not(.hidden)');
        const label = inner?.querySelector(`label.bulk-buy[data-qty="${qty}"]`);
        if (!label) return false;
        const input = label.querySelector('input[name="bulk_buy_qty"]');
        if (input?.checked) return false;
        label.click();
        return true;
      }

      /** Disabled inputs are left out of FormData, so Buy Once never sends a plan. */
      syncPlanInput() {
        if (!this.planInput) return;
        this.planInput.disabled = !this.querySelector('.po-radio--sub:checked');
      }

      onOutsideChange(event) {
        const target = event.target;
        if (!target?.closest) return;

        // The cart drawer and cart page use the same stepper classes, and their quantities
        // have nothing to do with what these cards are pricing.
        if (target.closest('cart-drawer') || target.closest('cart-items')) return;

        const isQuantity =
          target.closest('label.bulk-buy') ||
          target.closest('.bulkbuy-input-element') ||
          target.closest('.quantity__input') ||
          target.closest('.quantity__button');

        // The stepper writes its value after the click, so wait a frame
        if (isQuantity) requestAnimationFrame(this.update);
      }

      /* ── State ─────────────────────────────────────────────────────────── */

      getPrices() {
        const variants = this.data.variants || {};
        const id = this.root.querySelector('.product-variant-id')?.value;
        if (id && variants[id]) return variants[id];
        const first = Object.keys(variants)[0];
        return first ? variants[first] : null;
      }

      byId(id) {
        return this.plans.find((p) => String(p.id) === String(id));
      }

      /** Months that `units` of this product covers, for one person working through them. */
      monthsFor(units) {
        // Days are multiplied before converting to months, not after. Rounding first
        // overstates anything that is not a whole number of months: a 45 day supply
        // is 2 months on its own, but 2 of them is 90 days — 3 months, not 4.
        return this.servings > 0
          ? Math.round((this.servings * units) / 30)
          : this.baseMonths * units;
      }

      /**
       * Which plan applies. The customer's own choice wins; otherwise the interval
       * stretches with quantity, on the assumption one person works through them.
       * Falls back to the default plan when the stretched interval has no plan, so
       * the card never shows an interval that is not billed.
       */
      getPlan(qty) {
        // Checked before the quantity rules, not after. The picker and the stretching are
        // separate settings, and either can be on alone — leaving this below the scaling
        // test meant that with scaling off the dropdown moved but the plan behind it did
        // not, so the card showed one interval and the cart got another.
        if (this.chosenPlanId) {
          const chosen = this.byId(this.chosenPlanId);
          if (chosen) return chosen;
        }

        if (!this.scaleWithQuantity || !this.baseMonths || qty < 1) {
          return this.byId(this.defaultPlanId) || null;
        }

        const wanted = this.monthsFor(qty);
        const exact = this.plans.find((p) => p.months === wanted);
        if (exact) return exact;

        // No plan for exactly that long (3 x 180 days = 540, but the group only has 180 and
        // 360): take the longest plan that does not outrun the pack, so the next delivery
        // never arrives before this one is used up - 540 days -> every 360 days (12 months).
        // Measured in days where the plan has them, so 45 / 90 / 135 day plans compare exactly.
        const wantedDays = this.servings > 0 ? this.servings * qty : wanted * 30;
        const planDays = (p) => p.days || p.months * 30;
        const fitting = this.plans
          .filter((p) => planDays(p) > 0 && planDays(p) <= wantedDays)
          .sort((a, b) => planDays(b) - planDays(a));
        return fitting[0] || this.byId(this.defaultPlanId) || null;
      }

      onPickerChange(event) {
        this.chosenPlanId = event.detail?.planId || null;
        this.update();
      }

      /**
       * A quantity change alters what an interval means — "every month" at two bottles
       * is not the same commitment as at three — so an earlier choice is dropped.
       * Called before the plan is resolved, or the first paint after a change would
       * still use the stale choice.
       *
       * Only while the interval stretches with quantity. With that off the store has
       * decided the two are independent, and silently throwing away a chosen interval
       * because the customer added a bottle would just look broken.
       */
      forgetChoiceIfQuantityChanged(qty) {
        if (this.scaleWithQuantity && this.pickerQty !== null && this.pickerQty !== qty) {
          this.chosenPlanId = null;
        }
        this.pickerQty = qty;
      }

      /**
       * Keep the dropdown showing whichever plan is active. The options themselves are
       * server-rendered from the selling plan group, so nothing is built here — this
       * only moves the selection.
       */
      syncPicker(activePlan) {
        if (this.dropdown && activePlan) this.dropdown.setSelected?.(activePlan.id);
      }

      /**
       * The perks follow the chosen interval, not the product's own supply: a customer
       * taking delivery every 90 days is on the 90 day programme. Tier is picked by
       * rounding the interval up to the next tier, matching the Liquid.
       */
      renderPerks(activePlan) {
        if (!this.benefitsEl || !activePlan) return;

        const tiers = this.data.tiers || [];
        const labels = this.data.orderLabels || [];
        if (!tiers.length) return;

        // The plan's exact interval (45 days), not the rounded months (2 = 60 days), so the
        // tier matches the programme the customer is actually billed on.
        const days = activePlan.days || activePlan.months * 30;
        const tier =
          tiers[this.lockedTier - 1] ||
          tiers.find((t) => t.days > 0 && days <= t.days) ||
          tiers[tiers.length - 1];
        const perks = tier?.perks || [];

        // Same rule as the snippet: one line is not a sequence, so it carries no label
        const filled = perks.filter(Boolean).length;
        const rows = perks
          .map((perk, i) => {
            if (!perk) return '';
            const label =
              this.showOrderNumbers && filled > 1 && labels[i]
                ? `<span class="po-benefit__order">${labels[i]}:</span> `
                : '';
            return `<li>${label}${perk}</li>`;
          })
          .filter(Boolean)
          .join('');

        if (!rows) {
          this.benefitsEl.hidden = true;
          return;
        }
        this.benefitsEl.hidden = false;
        // Two tiers can share a day count, so a stated tier keys off its own index
        const key = this.lockedTier ? 'stated-' + this.lockedTier : String(tier.days);
        if (this.benefitsEl.dataset.tierDays !== key) {
          this.benefitsEl.dataset.tierDays = key;
          this.benefitsEl.innerHTML = rows;
        }
      }

      /** The checked bulk tile, if the tiles are on the page. */
      getBulkLabel() {
        if (!this.hasBulkBuy) return null;
        const visible = this.root.querySelectorAll('.main-bulk-buy .bulk-buy-inner:not(.hidden)');
        for (const inner of visible) {
          const checked = inner.querySelector('input[name="bulk_buy_qty"]:checked');
          if (checked) return checked.closest('label');
        }
        return null;
      }

      getQuantity() {
        const bulk = this.getBulkLabel();
        if (bulk) return Math.max(1, parseInt(bulk.dataset.qty || '1', 10));

        const input = this.root.querySelector('.price_quantity_wrapper .quantity__input');
        return Math.max(1, parseInt(input?.value || '1', 10));
      }

      getBulkDiscount() {
        const bulk = this.getBulkLabel();
        return bulk ? parseFloat(bulk.dataset.bulkDiscount || '0') || 0 : 0;
      }

      /**
       * What the cart charges for `qty` units at `unit` with the multipack discount on.
       * The cart takes the discount off each unit, in whole pence rounded down, and then
       * multiplies — not off the line total. Pricing the line as a whole drifted by a
       * penny or two: 2 x £14.49 less 10% is £26.08 as a total but £26.10 in the cart
       * (£14.49 - £1.44, twice). Same rule as bulk-buy.liquid and applyUnitPennyDiscount()
       * in product-custom.js, so the Buy Once card matches the selected tile. Used for
       * both cards: the SAVE10 / SAVE25 multipack discounts apply to subscription lines
       * too, on top of the selling plan price, so Subscribe & Save is the Buy Once price
       * less the plan's first-order percentage, rounded the same way the cart rounds it.
       */
      lineTotal(unit, qty, multiplier) {
        const discount = Math.floor(unit * (1 - multiplier) + 1e-6);
        return (unit - discount) * qty;
      }

      /**
       * Multipack % for `qty` units, from Theme settings > Multiple Quantity Discount as
       * bulk-buy.liquid hands them over — the same rule as getPackDiscountPercent() in
       * product-custom.js: 1, 2, and 3 or more. 0 when the page has no multipack.
       */
      packDiscountFor(qty) {
        const wrapper = this.hasBulkBuy ? this.root.querySelector('.main-bulk-buy') : null;
        if (!wrapper) return 0;
        const key = qty <= 1 ? 'discountOne' : qty === 2 ? 'discountTwo' : 'discountThree';
        return Number(wrapper.dataset[key]) || 0;
      }

      /**
       * The struck-through price for a subscription of `qty` units costing `total`, worked
       * out exactly as the cart drawer does it for that line (cart-drawer.liquid), so the
       * page and the cart never strike through two different figures. With a discount on
       * the line the cart strikes through its original price, the subscription price times
       * quantity. With nothing off (a single unit, no multipack discount) it falls back to
       * the product's compare-at price, times quantity. 0 means nothing to strike through.
       */
      subCompare(subUnit, qty, total) {
        const original = subUnit * qty;
        if (original !== total) return original;

        const productCompare = Number(this.data.productCompareAt) || 0;
        // The cart's own test compares the line price to the unit compare-at, kept as is
        if (productCompare > 0 && original !== productCompare) return productCompare * qty;
        return 0;
      }

      /* ── Render ────────────────────────────────────────────────────────── */

      update() {
        const prices = this.getPrices();
        if (!prices) return;

        const qty = this.getQuantity();
        const multiplier = 1 - this.getBulkDiscount() / 100;

        this.forgetChoiceIfQuantityChanged(qty);

        // The plan can change with quantity, so resolve it before pricing and keep
        // the label and the cart input on the same plan.
        const plan = this.getPlan(qty);
        if (plan) {
          if (this.planInput) this.planInput.value = plan.id;
          if (this.freqEl && plan.label) this.freqEl.textContent = plan.label;
        }
        this.syncPicker(plan);
        this.renderPerks(plan);

        const subUnit = (plan && prices.plans?.[plan.id]) ?? prices.price;

        const onceTotal = this.lineTotal(prices.price, qty, multiplier);
        // The multipack discount (Theme settings > Multiple Quantity Discount, charged in the
        // cart by the SAVE10 / SAVE25 automatic discounts) applies to subscription lines too,
        // taken off the selling plan price per unit exactly as it is for Buy Once.
        const subTotal = this.lineTotal(subUnit, qty, multiplier);

        this.paint(this.onceCard, onceTotal, 0, qty);
        this.paint(this.subCard, subTotal, this.subCompare(subUnit, qty, subTotal), qty);

        // Whichever card is selected is what the customer will actually pay, so that is
        // the figure the points line and the sticky buy bar have to be worked out from.
        const subscribing = this.querySelector('.po-radio--sub:checked') !== null;
        this.paintStickyPrice(subscribing, subUnit, prices.price, subTotal, onceTotal, qty);
        this.paintPoints(subscribing ? subTotal : onceTotal);

        if (this.packsOn) {
          this.paintPacks(prices, qty);
          this.paintNextDelivery(plan);
          this.paintButton(subscribing, subscribing ? subTotal : onceTotal, plan);
        }
      }

      /**
       * The product page's own Add to Basket (not the sticky bar's, which shares the
       * main_buy_button class) reads "Subscribe · £9.49 every 90 days" or
       * "Add to basket · £9.99", from the same total the selected card shows. Only the
       * ordinary add-to-basket label is replaced: sold out, pre-order and "Buy it Again"
       * keep their own wording, and a disabled button is left alone.
       */
      paintButton(subscribing, total, plan) {
        // Only this product's own button: not the sticky bar's (same class). In a quick view
        // that is the popup's button; on the product page, any button outside a quick view.
        const inQuickView = this.root !== document;
        const buttons = Array.from(this.root.querySelectorAll('button.main_buy_button')).filter(
          (button) =>
            !button.closest('.main-add-to-basket') &&
            (inQuickView || !button.closest('quick-add-modal, .quick-add-modal'))
        );

        buttons.forEach((button) => {
          const label = button.querySelector('.product-form__submit-text');
          if (!label) return;
          if (button.dataset.poOriginal === undefined) {
            button.dataset.poOriginal = label.textContent.trim();
          }
          const original = button.dataset.poOriginal.toLowerCase();
          const isAddLabel = original.includes('basket') || original.includes('cart') || original === '';
          if (button.disabled || !isAddLabel) return;

          button.classList.add('po-atc');

          let text = '';
          if (subscribing) {
            // Just "Subscribe · £9.49": the interval is already shown in the Delivery frequency picker
            text = `<strong>${this.dataset.tBtnSub || 'Subscribe'}</strong> · ${this.money(total)}`;
          } else {
            text = `<strong>${this.dataset.tBtnAdd || 'Add to basket'}</strong> · ${this.money(total)}`;
          }
          label.innerHTML = text;

        });
      }

      /* ── Pack rows ─────────────────────────────────────────────────────── */

      t(key, token, value) {
        return (this.dataset[key] || '').replace(`[${token}]`, value);
      }

      /** Multipack % the tile for `qty` carries - the same figure getBulkDiscount() reads. */
      tileDiscount(qty) {
        const inner = this.root.querySelector('.main-bulk-buy .bulk-buy-inner:not(.hidden)');
        const label = inner?.querySelector(`label.bulk-buy[data-qty="${qty}"]`);
        return label ? parseFloat(label.dataset.bulkDiscount || '0') || 0 : 0;
      }

      /**
       * Every row priced with the same helpers the cards use - lineTotal() for the cart's
       * per-unit penny rounding, getPlan() for the plan that quantity would put the
       * customer on - so a row shows exactly what the card shows once it is picked.
       */
      paintPacks(prices, qty) {
        const base = prices.price;
        let fromUnit = Infinity;
        let extraPct = 0;

        this.querySelectorAll('.po-pack').forEach((row) => {
          const q = parseInt(row.dataset.qty || '1', 10);
          const isSub = !!row.closest('.po-card--sub');
          const pct = this.tileDiscount(q);
          const multiplier = 1 - pct / 100;

          let total;
          let was = 0;
          let chips = '';
          let save = '';

          if (isSub) {
            const plan = this.getPlan(q);
            const unit = (plan && prices.plans?.[plan.id]) ?? base;
            total = this.lineTotal(unit, q, multiplier);
            const subPct = base > 0 ? Math.round(((base - unit) / base) * 100) : 0;
            if (q === 1) extraPct = subPct;
            if (total / q < base) was = base;
            const parts = [];
            if (pct > 0) parts.push(`<span class="po-chip po-chip--multi">${this.t('tMulti', 'percent', pct)}</span>`);
            if (subPct > 0) parts.push(`<span class="po-chip po-chip--sub">${this.t('tSub', 'percent', subPct)}</span>`);
            chips = parts.join('<span class="po-chip__plus">+</span>');
          } else {
            total = this.lineTotal(base, q, multiplier);
            fromUnit = Math.min(fromUnit, total / q);
            if (pct > 0) save = this.t('tSave', 'percent', pct);
          }

          const selected = q === qty;
          row.classList.toggle('is-selected', selected);
          row.setAttribute('aria-checked', selected ? 'true' : 'false');

          const chipsEl = row.querySelector('.po-pack__chips');
          if (chipsEl) {
            chipsEl.innerHTML = chips;
            chipsEl.hidden = !chips;
          }
          const saveEl = row.querySelector('.po-pack__save');
          if (saveEl) {
            saveEl.textContent = save;
            saveEl.hidden = !save;
          }
          const wasEl = row.querySelector('.po-pack__was');
          if (wasEl) {
            wasEl.textContent = this.money(was);
            wasEl.hidden = !was;
          }
          const unitEl = row.querySelector('.po-pack__unit');
          if (unitEl) unitEl.textContent = this.money(q > 1 ? total / q : total);
          const perEl = row.querySelector('.po-pack__per');
          if (perEl) perEl.hidden = q < 2;
          // Whole saving per unit against the full one-time price (multi-buy and subscribe together)
          const saveAllEl = row.querySelector('.po-pack__saveall');
          if (saveAllEl) {
            const allPct = base > 0 ? Math.round(((base - total / q) / base) * 100) : 0;
            saveAllEl.textContent = allPct > 0 ? this.t('tSave', 'percent', allPct) : '';
            saveAllEl.hidden = !(allPct > 0);
          }
          const totalEl = row.querySelector('.po-pack__total');
          if (totalEl) {
            totalEl.textContent = `${this.money(total)} ${this.dataset.tTotal || ''}`.trim();
            totalEl.hidden = q < 2;
          }
        });

        const fromEl = this.querySelector('[data-po-from]');
        if (fromEl && Number.isFinite(fromUnit)) fromEl.textContent = this.t('tFrom', 'price', this.money(fromUnit));

        const extraEl = this.querySelector('[data-po-extra]');
        if (extraEl) {
          extraEl.textContent = this.t('tExtra', 'percent', extraPct);
          extraEl.hidden = !(extraPct > 0);
        }
      }

      /** "Next delivery around ..." from today plus the interval of the plan that applies. */
      paintNextDelivery(plan) {
        const el = this.querySelector('[data-po-next]');
        if (!el) return;
        // Exact days from the plan ("Deliver every 45 days" -> +45 days); months only when the
        // plan was named in months and has no day count.
        const days = plan?.days || 0;
        const months = plan?.months || 0;
        if (!days && !months) {
          el.hidden = true;
          return;
        }
        const date = new Date();
        const namedInMonths = /month/i.test(plan?.label || '');
        if (namedInMonths && months) date.setMonth(date.getMonth() + months);
        else if (days) date.setDate(date.getDate() + days);
        else date.setMonth(date.getMonth() + months);
        const locale = document.documentElement.lang || 'en-GB';
        const formatted = date.toLocaleDateString(locale.length === 2 ? `${locale}-GB` : locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
        el.textContent = this.t('tNext', 'date', formatted);
        el.hidden = false;
      }

      /**
       * The sticky buy bar is a section of its own, outside this element, and the theme's
       * updatePriceDisplay() paints it from the one-time price on every quantity change.
       * Its Add to Basket already sends the selling plan — product-form.js looks this card
       * up by variant and sets it there — so while Subscribe & Save was chosen the bar was
       * quoting a price its own button does not charge. Repainted here from the same two
       * totals the cards use, and from inside update() so it follows a card, quantity,
       * variant or interval change alike.
       */
      paintStickyPrice(subscribing, subUnit, onceUnit, subTotal, onceTotal, qty) {
        const containers = document.querySelectorAll('.main-add-to-basket .price__container');
        if (!containers.length) return;

        if (!subscribing) {
          // Handing the bar back is delegated to updatePriceDisplay() rather than rebuilt
          // here: the one-time figure carries the multipack discount and its own compare-at
          // rules, and a second copy of those would drift out of step.
          // Only worth undoing if this actually took the bar over — otherwise the theme's
          // own painter still owns it and re-running it would be wasted work.
          if (!this.stickyPriceOwned) return;
          this.stickyPriceOwned = false;
          if (typeof window.updatePriceDisplay === 'function') {
            window.updatePriceDisplay(this.stickyQuantity(qty));
          }
          return;
        }

        this.stickyPriceOwned = true;

        containers.forEach((container) => {
          // updatePriceDisplay() publishes the one-time total it last rendered into this
          // very container, for the quantity THAT BAR is on. Read back rather than reusing
          // the cards' own total, because the two quantities can differ: the bar's stepper
          // can be moved past the picked multipack, and product-form.js then charges for the
          // stepper. Working from the published figure also leaves the multipack discount
          // rules in one place. Before that function has ever run there is nothing to read,
          // and the cards' total is right, because nothing has moved the two apart yet.
          const published = Number(container.dataset.onceTotal);
          const once = published > 0 ? published : onceTotal;

          // The multipack discount applies to the subscription line as well. When the bar is
          // on the cards' own basket that is the Subscribe card's total, used as is; otherwise
          // the bar's own quantity takes the pack rate for that quantity from the same
          // customizer settings the tiles are built from.
          const sameBasket = once === onceTotal || !(onceUnit > 0);
          const barQty = sameBasket ? qty : this.stickyQuantity(qty);
          const total = sameBasket
            ? subTotal
            : this.lineTotal(subUnit, barQty, 1 - this.packDiscountFor(barQty) / 100);

          // Same struck-through figure as the Subscribe card and the cart drawer.
          const was = this.subCompare(subUnit, barQty, total);
          const sale = `<span class="price-item price-item--sale price-item--last">${this.money(total)}</span>`;
          const compare =
            was > total
              ? `<s class="price-item price-item--compare price-item--regular">${this.money(was)}</s>`
              : '';

          container.innerHTML = sale + compare;
        });
      }

      /**
       * The quantity the sticky buy bar is on, which is its own stepper whenever that has a
       * value - the same rule product-form.js applies when the bar's button is the one
       * pressed, so what is quoted there is what is charged. Falls back to the cards' own
       * quantity when the bar has no stepper to read.
       */
      stickyQuantity(fallback) {
        const input = document.querySelector('.main-add-to-basket .price_quantity_wrapper .quantity__input');
        const stepped = parseInt(input?.value, 10);
        return stepped > 0 ? stepped : fallback;
      }

      paint(card, total, compare, qty) {
        if (!card) return;

        const value = card.querySelector('.po-value');
        if (value) value.textContent = this.money(total);

        const compareEl = card.querySelector('.po-compare');
        if (compareEl) {
          compareEl.textContent = this.money(compare);
          compareEl.hidden = compare <= total;
        }

        const perDay = card.querySelector('.po-perday');
        if (perDay) {
          // Days of supply scales with quantity
          const days = this.servings * qty;
          perDay.hidden = days < 1;
          if (days >= 1) perDay.textContent = `${this.money(total / days)} ${this.perDayLabel}`;
        }
      }

      /**
       * The WeightWorld points line sits outside this element, and the theme's own
       * scripts write it from the one-time price on a variant or bulk tile change.
       * Subscribing charges less than that, so left alone the page offered points for a
       * price it was not charging. Rewritten here from the selected card's total, and
       * from inside update() so it follows a card, quantity, variant or interval change
       * alike.
       *
       * floor, and one point per unit of currency, matching the rule the theme's other
       * writers already use — 9.99 spent earns 9, not 10.
       */
      paintPoints(total) {
        const points = document.getElementById('comfortPoints');
        if (!points) return;
        points.textContent = String(Math.floor(total / 100));
      }

      money(cents) {
        const format = (amount, decimals, thousands, decimal) => {
          const fixed = (amount / 100).toFixed(decimals);
          const parts = fixed.split('.');
          const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
        };

        const value = Math.round(cents);

        return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => {
          switch (token) {
            case 'amount':
              return format(value, 2, ',', '.');
            case 'amount_no_decimals':
              return format(value, 0, ',', '.');
            case 'amount_with_comma_separator':
              return format(value, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return format(value, 0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return format(value, 2, "'", '.');
            default:
              return match;
          }
        });
      }
    }
  );
}
